import React, { useContext } from 'react';
import { Pagination } from 'baseui/pagination';
import { Plus, Delete, Check, DeleteAlt, Upload } from 'baseui/icon';
import { StatefulDataTable } from 'baseui/data-table';
import _isFunction from 'lodash/isFunction';
import _noop from 'lodash/noop';

import Footer from '../Footer';
import { EMPTY_MAP, EMPTY_OBJECT } from '../../constants';
import { getInitialFilters } from '../../utils/filters';
import AppContext from '../../context/app/appContext';
import ConfirmationModal from '../ConfirmationModal';

import { IDataListProps } from './types';
import useDataList from './useDataList';
import useStyles from './useStyles';

const FOOTER_MESSAGE = 'This page is maintained by tech team, for any clarification please contact tech team.';

const DataList: React.FC<IDataListProps> = (props) => {
	const {
		fetchData,
		columnConfigs,
		actions = EMPTY_OBJECT,
		hideActions,
		getActionHandler,
		defaults,
		initialFilters = EMPTY_MAP,
		rowHeight,
		entity,
		initialSortDirection,
		initialSortIndex,
		disablePagination,
		height,
		filterable = true,
		searchable = true,
		fullDataSearch = false,
		globalForceUpdate,
		turnGlobalForceUpdateOff,
		showFooterMessage = true,
		disablePaginationSelect = false,
		isPreProd = false
	} = props;

	const { listStyle, actionStyle, individualStyle } = useStyles({ height });
	const { app } = useContext(AppContext);

	const {
		rows,
		columns,
		loading = false,
		emptyMessage,
		confirmationDialog,
		pageDetails,
		rowCount,
		batchActions,
		handleAddFilter,
		handleRemoveFilter,
		handleAlertCancel,
		handleAlertConfirmation,
		handlePageChange,
		setForceUpdateRows,
		handleTextQueryChange
	} = useDataList({
		fetchData,
		columnConfigs,
		actions,
		getActionHandler,
		app,
		defaults,
		entity,
		hideActions,
		globalForceUpdate,
		turnGlobalForceUpdateOff,
		isPreProd
	});

	return (
		<>
			{confirmationDialog.show && (
				<ConfirmationModal
					confirmationHeader={confirmationDialog.header}
					confirmationMessage={confirmationDialog.message}
					onClose={handleAlertCancel}
					confirmationDialogOnAgree={handleAlertConfirmation}
				/>
			)}
			<div>
				<div className={listStyle}>
					<StatefulDataTable
						onFilterAdd={handleAddFilter}
						onFilterRemove={handleRemoveFilter}
						columns={columns}
						initialFilters={getInitialFilters(entity, initialFilters)}
						rows={rows}
						batchActions={batchActions}
						loading={loading}
						rowHeight={rowHeight}
						initialSortDirection={initialSortDirection}
						initialSortIndex={initialSortIndex}
						filterable={filterable}
						searchable={searchable}
						emptyMessage={emptyMessage}
						onTextQueryChange={(searchText: string) => handleTextQueryChange(searchText, fullDataSearch)}
					/>
					{showFooterMessage && <Footer message={FOOTER_MESSAGE} />}
				</div>
				{!disablePagination && (
					<Pagination
						currentPage={pageDetails.pageNo}
						numPages={Math.ceil(rowCount / pageDetails.pageSize)}
						onPageChange={({ nextPage }) => {
							handlePageChange({ ...pageDetails, pageNo: nextPage });
						}}
						overrides={
							disablePaginationSelect
								? {
										Select: {
											props: {
												overrides: {
													SelectArrow: {
														props: {
															overrides: {
																style: () => ({
																	display: 'none'
																})
															}
														}
													}
												}
											}
										}
								  }
								: {}
						}
					/>
				)}
				<div className={actionStyle}>
					{actions.addAction && (
						<div
							role='presentation'
							className={individualStyle}
							onClick={
								_isFunction(getActionHandler) ? getActionHandler(actions.addAction, { setForceUpdateRows }) : _noop
							}>
							<Plus size={50} />
						</div>
					)}
					{actions.backAction && (
						<div
							role='presentation'
							className={individualStyle}
							onClick={
								_isFunction(getActionHandler) ? getActionHandler(actions.backAction, { setForceUpdateRows }) : _noop
							}>
							<Delete size={50} />
						</div>
					)}
					{actions.submitAction && (
						<div
							role='presentation'
							className={individualStyle}
							onClick={
								_isFunction(getActionHandler) ? getActionHandler(actions.submitAction, { setForceUpdateRows }) : _noop
							}>
							<Check size={50} />
						</div>
					)}
					{actions.clearAction && (
						<div
							role='presentation'
							className={individualStyle}
							onClick={
								_isFunction(getActionHandler) ? getActionHandler(actions.clearAction, { setForceUpdateRows }) : _noop
							}>
							<DeleteAlt size={50} />
						</div>
					)}
					{actions.uploadAction && (
						<div
							role='presentation'
							className={individualStyle}
							onClick={
								_isFunction(getActionHandler) ? getActionHandler(actions.uploadAction, { setForceUpdateRows }) : _noop
							}>
							<Upload size={50} />
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default DataList;
